import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { DOCUMENT, Location } from '@angular/common';
import { Router } from '@angular/router';
import { EMPTY, Observable, finalize, firstValueFrom, shareReplay, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { User } from '../models/auth.interface';

type SignInResponse = { expires_at: number } | { requires2fa: true; mfaToken: string };

const MFA_TOKEN_KEY = 'backoffice.mfa_token';

/**
 * The session is an HttpOnly cookie the API sets and only honours on requests from this origin, so
 * no token is ever readable from script here. `currentUser` is the only client-side session state: it is filled
 * at startup from /auth/me and after a sign-in, and cleared on logout or a 401.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private location = inject(Location);
  private document = inject(DOCUMENT);

  private readonly apiUrl = environment.apiUrl;
  private readonly baseApiUrl = environment.baseApiUrl;

  readonly currentUser = signal<User | null>(null);
  private readonly csrfToken = signal<string | null>(null);
  // Shared in-flight CSRF fetch so concurrent callers reuse one request
  // instead of each triggering a separate token round-trip.
  private csrfRefresh$: Observable<{ token: string }> | null = null;

  getCsrfTokenValue(): string | null {
    return this.csrfToken();
  }

  fetchCsrfToken(): Observable<{ token: string }> {
    // Coalesce concurrent refreshes: if a fetch is already in flight, hand back
    // the same observable so we don't fire N parallel token requests.
    if (!this.csrfRefresh$) {
      // The CSRF route lives at the API root, not under the /backoffice prefix.
      this.csrfRefresh$ = this.http
        .get<{ token: string }>(`${this.baseApiUrl}/auth/csrf-token`, { withCredentials: true })
        .pipe(
          tap((res) => this.csrfToken.set(res.token)),
          finalize(() => (this.csrfRefresh$ = null)),
          shareReplay(1),
        );
    }
    return this.csrfRefresh$;
  }

  /** Password sign-in, as in the app. An operator with 2FA is sent on to the code step. */
  login(credentials: { email: string; password: string }) {
    // Prime the CSRF cookie + token before the login POST, otherwise the
    // server's double-submit CSRF check rejects the request with a 403.
    return this.fetchCsrfToken().pipe(
      switchMap(() => this.http.post<SignInResponse>(`${this.apiUrl}/auth/login`, credentials)),
      switchMap((res) => {
        if (!('requires2fa' in res)) return this.completeSignIn();
        this.setMfaToken(res.mfaToken);
        this.router.navigate(['/login/verify-2fa'], { queryParamsHandling: 'preserve' });
        return EMPTY;
      }),
    );
  }

  /** The 2FA challenge from a password or Google sign-in, until the code step spends it. */
  setMfaToken(token: string): void {
    sessionStorage.setItem(MFA_TOKEN_KEY, token);
  }

  getMfaToken(): string | null {
    return sessionStorage.getItem(MFA_TOKEN_KEY);
  }

  verifyLogin2fa(mfaToken: string, code: string, method: 'totp' | 'recovery') {
    return this.fetchCsrfToken().pipe(
      switchMap(() =>
        this.http.post(`${this.apiUrl}/auth/2fa/login-verify`, { mfaToken, code, method }),
      ),
      tap(() => sessionStorage.removeItem(MFA_TOKEN_KEY)),
      switchMap(() => this.completeSignIn()),
    );
  }

  /** The API has set the session cookie; load who signed in and go where they were headed. */
  private completeSignIn() {
    return this.getUser().pipe(
      tap((user) => {
        this.currentUser.set(user);
        this.router.navigateByUrl(this.returnUrlFromRoute() ?? '/dashboard');
      }),
    );
  }

  /**
   * Hands the whole page to the API's Google flow, which comes back to the page they were headed
   * for, to /login/verify-2fa when the operator has 2FA, or to /login?error=.... A full redirect
   * rather than a popup: nothing to postMessage back.
   */
  loginWithGoogle(): void {
    this.document.location.assign(this.googleSignInUrl());
  }

  /** Split out of loginWithGoogle because jsdom will not let a test stub location.assign. */
  googleSignInUrl(): string {
    const returnUrl = this.returnUrlFromRoute() ?? '/dashboard';
    return `${this.apiUrl}/auth/google?returnUrl=${encodeURIComponent(returnUrl)}`;
  }

  logout(): void {
    // The HttpOnly cookie is the session, and only the API can clear it, so wait for the answer
    // before leaving. If the call fails the cookie may still be live: the next load of /login
    // finds the session and guestGuard sends them back in, rather than showing a false sign-out.
    this.http.post(`${this.apiUrl}/auth/logout`, {}).subscribe({
      complete: () => {
        this.clearSession();
        this.router.navigate(['/login']);
      },
      error: () => this.resolveUserFromSession(),
    });
  }

  /**
   * The session ended under the user (an expired or revoked token). Unlike logout, keeps the page
   * they were on so signing in returns them there. Several requests can 401 at once; only the first,
   * still off /login, records a return address.
   */
  expireSession(): void {
    this.clearSession();
    // The browser's address, not router.url: at startup (resolveUserFromSession runs before the first
    // navigation) router.url is still '/', and a Slack link opened with a stale session would be lost.
    const current = this.location.path() || '/';
    if (current.startsWith('/login')) return;
    this.router.navigate(['/login'], { queryParams: { returnUrl: current } });
  }

  private clearSession(): void {
    this.currentUser.set(null);
    this.csrfToken.set(null);
  }

  getUser(): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/auth/me`);
  }

  resolveUserFromSession(): Promise<void> {
    // ponytail: the bearer token this app used to keep. Drop this line once every operator has
    // loaded a release with cookie sessions.
    localStorage.removeItem('backoffice_auth_token');

    return (
      firstValueFrom(this.getUser())
        .then((user) => {
          this.currentUser.set(user);
        })
        // A 401 has already gone through the interceptor's expireSession(); anything else just means
        // "not signed in".
        .catch(() => this.currentUser.set(null))
    );
  }

  private returnUrlFromRoute(): string | null {
    return safeReturnUrl(this.router.parseUrl(this.router.url).queryParamMap.get('returnUrl'));
  }
}

/**
 * Only a path inside this app: `//evil.example` and `/\\evil.example` read as other origins to a
 * browser, and a return to /login itself would loop.
 */
export function safeReturnUrl(value: string | null): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\'))
    return null;
  if (value.startsWith('/login')) return null;
  return value;
}
